import { LocalAeqdProjection } from "./projection";
import type { LatLng } from "./types";

export interface PolygonEnvelope {
  center: LatLng;
  radiusMeters: number;
  areaSquareMeters: number;
}

export type PolygonIssue =
  | "too-few-points"
  | "too-many-points"
  | "duplicate-points"
  | "self-intersection"
  | "zero-area";

export interface PolygonValidation {
  valid: boolean;
  issues: PolygonIssue[];
  envelope?: PolygonEnvelope;
}

function unwrapLongitude(longitude: number, reference: number): number {
  let value = longitude;
  while (value - reference > 180) value -= 360;
  while (value - reference < -180) value += 360;
  return value;
}

function normalizeLongitude(longitude: number): number {
  let value = longitude;
  while (value > 180) value -= 360;
  while (value < -180) value += 360;
  return value;
}

export function polygonEnvelope(vertices: LatLng[]): PolygonEnvelope | undefined {
  if (vertices.length < 3) return undefined;
  const referenceLongitude = vertices[0]?.lng ?? 0;
  const center = {
    lat: vertices.reduce((sum, point) => sum + point.lat, 0) / vertices.length,
    lng: normalizeLongitude(
      vertices.reduce((sum, point) => sum + unwrapLongitude(point.lng, referenceLongitude), 0) / vertices.length,
    ),
  };
  const projection = new LocalAeqdProjection(center);
  const projected = vertices.map((point) => projection.forward(point));
  const radiusMeters = projected.reduce(
    (maximum, point) => Math.max(maximum, Math.hypot(point.x, point.north)),
    0,
  );
  let signedArea = 0;
  for (let index = 0; index < projected.length; index += 1) {
    const current = projected[index];
    const next = projected[(index + 1) % projected.length];
    if (!current || !next) continue;
    signedArea += current.x * next.north - next.x * current.north;
  }
  return {
    center,
    radiusMeters,
    areaSquareMeters: Math.abs(signedArea) / 2,
  };
}

function orientation(
  a: { x: number; north: number },
  b: { x: number; north: number },
  c: { x: number; north: number },
): number {
  return (b.x - a.x) * (c.north - a.north) - (b.north - a.north) * (c.x - a.x);
}

function onSegment(
  a: { x: number; north: number },
  b: { x: number; north: number },
  point: { x: number; north: number },
): boolean {
  const epsilon = 1e-7;
  return Math.abs(orientation(a, b, point)) <= epsilon
    && point.x >= Math.min(a.x, b.x) - epsilon
    && point.x <= Math.max(a.x, b.x) + epsilon
    && point.north >= Math.min(a.north, b.north) - epsilon
    && point.north <= Math.max(a.north, b.north) + epsilon;
}

function segmentsIntersect(
  a: { x: number; north: number },
  b: { x: number; north: number },
  c: { x: number; north: number },
  d: { x: number; north: number },
): boolean {
  const abC = orientation(a, b, c);
  const abD = orientation(a, b, d);
  const cdA = orientation(c, d, a);
  const cdB = orientation(c, d, b);
  if (((abC > 0 && abD < 0) || (abC < 0 && abD > 0))
    && ((cdA > 0 && cdB < 0) || (cdA < 0 && cdB > 0))) return true;
  return onSegment(a, b, c) || onSegment(a, b, d) || onSegment(c, d, a) || onSegment(c, d, b);
}

export function validatePolygonSelection(vertices: LatLng[], maximumPoints = 256): PolygonValidation {
  const issues: PolygonIssue[] = [];
  if (vertices.length < 3) issues.push("too-few-points");
  if (vertices.length > maximumPoints) issues.push("too-many-points");
  const envelope = polygonEnvelope(vertices);
  if (!envelope || envelope.areaSquareMeters < 0.01) issues.push("zero-area");
  if (!envelope) return { valid: false, issues };

  const projection = new LocalAeqdProjection(envelope.center);
  const projected = vertices.map((point) => projection.forward(point));
  const pointKeys = new Set<string>();
  for (const point of projected) {
    const key = `${Math.round(point.x * 1000)}:${Math.round(point.north * 1000)}`;
    if (pointKeys.has(key)) {
      issues.push("duplicate-points");
      break;
    }
    pointKeys.add(key);
  }

  for (let first = 0; first < projected.length; first += 1) {
    const firstNext = (first + 1) % projected.length;
    for (let second = first + 1; second < projected.length; second += 1) {
      const secondNext = (second + 1) % projected.length;
      if (first === second || firstNext === second || secondNext === first) continue;
      if (segmentsIntersect(
        projected[first]!,
        projected[firstNext]!,
        projected[second]!,
        projected[secondNext]!,
      )) {
        issues.push("self-intersection");
        first = projected.length;
        break;
      }
    }
  }

  return {
    valid: issues.length === 0,
    issues: [...new Set(issues)],
    envelope,
  };
}
